import React from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { EntityIdentityCell, StatusPill } from "components/admin";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildSchoolPlayerCreateValues,
  buildSchoolPlayerFields,
  buildSchoolPlayerUpdateValues,
} from "pages/app/workspace/sections/forms/school-ops-section-forms";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { workspaceApi } from "pages/app/workspace/services";
import { hasAnyPortalAction } from "utils/portal-access";

export const SchoolPlayersSection: React.FC<{ portalAccess: any }> = ({ portalAccess }) => {
  const history = useHistory();
  const { schoolId } = useScopedSchoolSelection({
    portalAccess,
  });

  const playersQuery = useQuery({
    queryKey: ["workspace-school-players", schoolId],
    queryFn: () => workspaceApi.schoolPlayers.list(schoolId),
    enabled: Boolean(schoolId),
  });
  const teamsQuery = useQuery({
    queryKey: ["workspace-school-player-teams", schoolId],
    queryFn: () => workspaceApi.schoolTeams.list(schoolId),
    enabled: Boolean(schoolId),
  });

  const canCreateSchoolPlayers = hasAnyPortalAction(portalAccess, [
    "school_players.create",
    "school_players.manage",
  ]);
  const canEditSchoolPlayers = hasAnyPortalAction(portalAccess, [
    "school_players.update",
    "school_players.manage",
  ]);
  const canRemoveSchoolPlayers = hasAnyPortalAction(portalAccess, [
    "school_players.delete",
    "school_players.manage",
    "players.manage",
  ]);
  const schoolPlayerFields = buildSchoolPlayerFields(teamsQuery.data || []);

  return (
    <CrudSection
      buildCreateValues={buildSchoolPlayerCreateValues}
      buildUpdateValues={buildSchoolPlayerUpdateValues}
      canCreate={canCreateSchoolPlayers && Boolean(schoolId)}
      canEdit={canEditSchoolPlayers && Boolean(schoolId)}
      canRemove={canRemoveSchoolPlayers && Boolean(schoolId)}
      canView
      columns={[
        {
          label: "Player",
          accessor: (row) => (
            <EntityIdentityCell
              fallbackLabel={row.full_name}
              imageAlt={row.full_name || "Player"}
              imageSrc={row.avatar_url || row.image_url || row.image}
              primaryText={row.full_name || "Unknown"}
              secondaryText={row.position || "Position pending"}
              variant="athlete"
            />
          ),
        },
        {
          label: "School",
          accessor: (row) => (
            <EntityIdentityCell
              fallbackLabel={row.school_name}
              imageAlt={row.school_name || "School"}
              imageSrc={row.school_image_url}
              primaryText={row.school_name || "Unknown"}
              secondaryText={row.profile_visibility || "Profile visibility pending"}
              variant="school"
              size="sm"
            />
          ),
        },
        { label: "Position", accessor: (row) => row.position || "Unknown" },
        { label: "Grade", accessor: (row) => row.school_grade || "Unknown" },
        { label: "Consent", accessor: (row) => <StatusPill value={row.consent_status} /> },
        {
          label: "Teams",
          accessor: (row) =>
            (row.school_teams || []).map((team: any) => team.name).join(", ") || "None",
        },
      ]}
      createLabel="Add school player"
      data={playersQuery.data || []}
      description={
        canCreateSchoolPlayers || canEditSchoolPlayers || canRemoveSchoolPlayers
          ? "Create, update, and review child player profiles for the selected school with guardian, consent, and team context captured in one form."
          : "Review child player profiles, guardian details, and consent status for the selected school."
      }
      fields={schoolPlayerFields}
      invalidateKeys={[["workspace-school-players", schoolId]]}
      isLoading={playersQuery.isLoading}
      onCreate={(payload) => workspaceApi.schoolPlayers.create(schoolId, payload)}
      onRemove={(row) => workspaceApi.schoolPlayers.remove(schoolId, row.id)}
      onUpdate={(row, payload) => workspaceApi.schoolPlayers.update(schoolId, row.id, payload)}
      onView={(row) => {
        if (!row?.id) {
          return;
        }

        history.push({
          pathname: `/workspace/school-players/${row.school_id || schoolId}/${row.id}`,
          state: {
            backTo: "/workspace/school-players",
          },
        });
      }}
      removeSuccessMessage="Player removed."
      saveSuccessMessage="Player saved."
      title="School Players"
    />
  );
};
